import { useContext, useEffect, useState } from 'react';
import {Badge, Button, Col, Empty, Popconfirm, Row, Table, Tooltip} from "antd";
import {BarChartOutlined, DeleteOutlined, EditOutlined, FileAddOutlined} from "@ant-design/icons";
import {DetailContext, ListContext, SearchContext} from "./JobListEffectComponent";
import * as GlobalConfig from "../../../../constants/GlobalConfig";
import {useDispatch} from "react-redux";
import * as Actions from "../../../../flex/Actions";
import moment from "moment";
import StatusTag from 'components/modules/StatusTag';

JobList.propTypes = {};

function JobList(props) {
    const [list, setList] = useContext(ListContext);
    const [search, setSearch] = useContext(SearchContext);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
            showTotal: (total, range) => {
                return (`全${total}件中 ${range[0]}-${range[1]} 件`)
            },
            pageSize: 50,
            total: list.notificationListItemCount,
            position: ["topRight", "bottomRight"],
            showSizeChanger: true,
            pageSizeOptions: GlobalConfig.PageSizeOptions,
            onShowSizeChange: (page, page_size) => {
                setPagination(pagination => ({
                    ...pagination,
                    page,
                    pageSize: page_size
                }));
                setSearch(search => ({
                    ...search,
                    page,
                    page_size
                }))
            },
            onChange: (page, page_size) => {
                setPagination(pagination => ({
                    ...pagination,
                    current: page,
                    pageSize: page_size
                }));
                setSearch(search => ({
                    ...search,
                    page,
                    page_size
                }))
            }
        }
    );
    const dispatch = useDispatch();

    useEffect(() => {
        setPagination(pagination => ({
            ...pagination,
            total: list.jobListItemCount
        }));
    }, [dispatch, list.jobList, list.jobListItemCount])

    useEffect(() => {
        setLoading(list.loading);
    }, [list.loading])


    useEffect(() => {
        const data = search;
        const page = search?.page || 1;
        setPagination(pagination => ({
            ...pagination,
            current: page
        }));
        if (!data?.page_size) {
            data.page_size = 50;
        }
        delete data.page;
        dispatch(Actions.http.connection.job.search(data, page));
    }, [dispatch, search]);


    function analyzeJSON(data, callback) {
        for (let key in data) {
            if (typeof data[key] === "object") {
                analyzeJSON(data[key], callback);
            } else {
                callback(`${key}:${data[key]}`)
            }
        }
    }

    /**
     * ミリ秒を時分秒へ変換
     * ms ミリ秒
     */
    function computeDuration(ms) {
        let h = String(Math.floor(ms / 3600000) + 100).substring(1);
        let m = String(Math.floor((ms - h * 3600000) / 60000) + 100).substring(1);
        let s = String(Math.round((ms - h * 3600000 - m * 60000) / 1000) + 100).substring(1);
        if(h !== "00"){
            return h + '時間' + m + '分' + s + '秒';
        }else if(m !== "00"){
            return m + '分' + s + '秒';
        }else{
            return s + '秒';
        }
    }

    const columns = [
        {
            title: '種別',
            width: "280px",
            dataIndex: 'type_name',
            key: 'type_name',
        },
        {
            title: '状態',
            width: "115px",
            dataIndex: 'is_success',
            key: 'is_success',
            render: (_, record) => {
                const priority = record.is_success;
                switch (priority) {
                    case true: {
                        return <StatusTag status="success">成功</StatusTag>;
                    }
                    case false: {
                        // ジョブのキャンセル状態を判定する
                        const isCancelled = record.message === 'タスクを再度実施してください。' && 
                            record.done_at !== null && 
                            record.is_success === false;
                        if (isCancelled) {
                            return <StatusTag status="warning">キャンセル</StatusTag>;
                        }
                        return <StatusTag status="error">失敗</StatusTag>;
                    }
                    case null: {
                        return <StatusTag status="processing">実施中</StatusTag>;
                    }
                    default: {
                        return <StatusTag status="default">不明</StatusTag>;
                    }
                }
            }
        },
        {
            title: '詳細情報',
            minWidth: "270px",
            dataIndex: 'result',
            key: 'result',
            render: (_, record) => {
                let content = [];
                analyzeJSON(_, (rVal) => {
                    content.push(rVal)
                })
                // ジョブのキャンセル状態を判定する
                const isCancelled = record.message === 'タスクを再度実施してください。' && 
                    record.done_at !== null && 
                    record.is_success === false;
                return (
                    <div>
                        {isCancelled && (
                            <>
                                <span>
                                    このタスクはキャンセルされました
                                </span>
                                <br/>
                            </>
                        )}
                        {(record.is_success === null || isCancelled) && record.message}
                        <br/>
                        {!isCancelled && content.map((v, index) => {
                            return <p key={index}>{v}</p>
                        })}
                    </div>
                )
            }
        },
        {
            title: '処理時間 (開始/終了/経過)',
            width: "220px",
            key: 'processing_time',
            render: (_, record) => {
                const start = moment.unix(record.created_at).format("YYYY/MM/DD HH:mm");
                const end = record.done_at ? moment.unix(record.done_at).format("YYYY/MM/DD HH:mm")
                    : null;
                const duration = record.done_at && record.created_at
                    ? computeDuration(moment.unix(record.done_at).diff(moment.unix(record.created_at)))
                    : null;

                return (
                    <div>
                        <div><span style={{color: 'rgba(0, 0, 0, 0.7)'}}>開始:</span> {start ?? '-'}</div>
                        <div><span style={{color: 'rgba(0, 0, 0, 0.7)'}}>終了:</span> {end ?? '-'}</div>
                        <div><span style={{color: 'rgba(0, 0, 0, 0.7)'}}>経過:</span> {duration ?? '-'}</div>
                    </div>
                )
            }
        }
    ];


    return (
        <div className="notifications-list container">
            <Table
                locale={{
                    filterConfirm: '確定',
                    filterReset: 'リセット',
                    emptyText: <Empty image={Empty.PRESENTED_IMAGE_DEFAULT} description={'実施している処理はありません'}/>,
                }}
                tableLayout='fixed'
                rowKey={"uuid"}
                bordered
                size="small"
                columns={columns}
                dataSource={list.jobList}
                scroll={{x: 830}}
                pagination={pagination}
                className="general-table"
                loading={list.loading}
            />
        </div>
    );
}

export default JobList;